/**
 * Situations browser — wraps lang-core's <StudyMode> with custom slots:
 *   - renderFront: English phrase + speaker icon + audio button + cultural badge
 *   - renderBack: kana translation + partner bubbles + register tag + notes
 *                 + "Say it aloud" nudge + Got it / Didn't know self-grade
 *   - extraControls: section-tab navigation hint
 *
 * Reuses the existing SRS engine via situationsSignals. Self-grade triggers
 * `updateWordSRS(cardId, isCorrect)` which advances the card's SRS state
 * machine identically to vocab cards.
 *
 * "Say it aloud" nudge follows the Pimsleur anticipation principle +
 * Conti 2025 shadowing systematic review — production rehearsal at the
 * moment of self-grade, no ASR required.
 */

import { StudyMode } from '@kevvykevwin/lang-core/components';
import type { Card } from '@kevvykevwin/lang-core/types';
import type { SrsSignals } from '@kevvykevwin/lang-core/srs';

import {
  CATEGORY_LABELS,
  type SituationCardExt,
  type SituationCategory
} from './types.js';
import { categoryCounts } from './index.js';
import { SituationsCategoryPicker } from './SituationsCategoryPicker.js';
import { SpeakerBubble } from './SpeakerBubble.js';
import { CulturalNoteBadge } from './CulturalNoteBadge.js';

interface Props {
  signals: SrsSignals<SituationCardExt>;
  mainLevelSetter: (level: string) => void;
  onPlayAudio: (text: string) => void;
  activeCategory: SituationCategory | 'all';
  setActiveCategory: (cat: SituationCategory | 'all') => void;
}

export function SituationsBrowser({
  signals,
  mainLevelSetter,
  onPlayAudio,
  activeCategory,
  setActiveCategory
}: Props) {
  const counts = categoryCounts();
  const currentLevel = signals.currentLevel.value;

  return (
    <div>
      <SituationsCategoryPicker
        active={activeCategory}
        counts={counts}
        onSelect={setActiveCategory}
      />

      <StudyMode<SituationCardExt>
        signals={signals}
        onPlayAudio={onPlayAudio}
        renderFront={card => <SituationFront card={card} onPlayAudio={onPlayAudio} />}
        renderBack={card => <SituationBack card={card} signals={signals} />}
        // Hide lang-core's built-in Previous / Flip / Next / Shuffle / Reset
        // row. Situations is a self-graded review flow — Got it / Didn't
        // know already advance via signals.nextCard(), and the card flips
        // implicitly on the next card's front face. The full nav row was
        // visually overwhelming alongside the grade buttons.
        hideControls
        caughtUpHeading={
          activeCategory === 'all'
            ? 'All caught up for this level!'
            : `No ${CATEGORY_LABELS[activeCategory]} situations due`
        }
        caughtUpMessage={
          activeCategory !== 'all'
            ? `No ${CATEGORY_LABELS[activeCategory]} situations at level ${currentLevel}. Try "All" or a higher CEFR level.`
            : currentLevel === 'C2' || currentLevel === 'Everyday'
              ? "You've seen every Situation at this level."
              : 'Switch to a higher level to see more Situations.'
        }
        keyboardHint={
          <span className="hidden sm:inline">
            Tap card to flip • Got it / Didn&apos;t know to advance
          </span>
        }
        loadingMessage="Loading Situations…"
      />

      {/* Slim level-up affordance — always visible at A1/A2 so beginners
          see the path forward, but small enough not to compete with the
          grade buttons for attention. */}
      {(currentLevel === 'A1' || currentLevel === 'A2') && (
        <div className="mt-4 text-center">
          <button
            type="button"
            onClick={() =>
              mainLevelSetter(currentLevel === 'A1' ? 'A2' : 'B1')
            }
            className="text-xs font-semibold text-primary-700 hover:text-primary-900 underline decoration-dotted underline-offset-4 transition-all"
          >
            Try {currentLevel === 'A1' ? 'A2' : 'B1'} situations →
          </button>
        </div>
      )}
    </div>
  );
}

// ============================================
// Front face — English phrase + audio + cultural badge
// ============================================

function SituationFront({
  card,
  onPlayAudio
}: {
  card: Card<SituationCardExt>;
  onPlayAudio: (text: string) => void;
}) {
  const ext = card as Card<SituationCardExt>;
  return (
    <div className="text-center space-y-4">
      <div className="flex items-center justify-center gap-3">
        {ext.speakers === 2 && (
          <span className="text-xs font-semibold uppercase tracking-wider text-amber-700 bg-amber-50 px-2 py-1 rounded-full">
            Dialogue
          </span>
        )}
        {ext.formality && (
          <span className="text-xs font-semibold uppercase tracking-wider text-slate-500 bg-slate-100 px-2 py-1 rounded-full">
            {ext.formality}
          </span>
        )}
      </div>

      <p className="text-3xl sm:text-4xl font-bold leading-tight text-slate-900">
        {card.front}
      </p>

      <button
        type="button"
        onClick={() => onPlayAudio(card.audioText ?? card.front)}
        className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold bg-white border border-slate-200 hover:bg-slate-50 transition-all"
        aria-label="Play audio"
      >
        <span aria-hidden="true">🔊</span>
        <span>Listen</span>
      </button>

      <CulturalNoteBadge pragmaticNote={ext.pragmaticNote} notes={ext.notes} />
    </div>
  );
}

// ============================================
// Back face — kana + partner + notes + grade
// ============================================

function SituationBack({
  card,
  signals
}: {
  card: Card<SituationCardExt>;
  signals: SrsSignals<SituationCardExt>;
}) {
  const ext = card as Card<SituationCardExt>;
  const isDialogue = ext.speakers === 2 && ext.partner;

  return (
    <div className="space-y-5">
      {/* English (small, secondary) */}
      <p className="text-center text-base text-slate-500">{card.front}</p>

      {/* Kana translation (large, primary). Use translations.ja (pure
          kana) per the user's "kana only" plan ask — `card.back` may carry
          a kanji+kana variant for richer reading later. */}
      <p className="text-center text-2xl sm:text-3xl font-japanese tracking-japanese font-medium text-slate-900">
        {ext.translations.ja}
      </p>

      {/* Dialogue bubbles */}
      {isDialogue && ext.partner && (
        <div className="flex flex-col gap-3 mt-4">
          <SpeakerBubble
            side="you"
            primary={card.front}
            secondary={ext.translations.ja}
          />
          <SpeakerBubble
            side="them"
            primary={ext.partner.en}
            secondary={ext.partner.ja}
          />
        </div>
      )}

      <CulturalNoteBadge pragmaticNote={ext.pragmaticNote} notes={ext.notes} />

      {/* Pimsleur "say it aloud" nudge */}
      <p className="text-center text-xs font-semibold uppercase tracking-wider text-primary-700 bg-primary-50 rounded-full py-2">
        🔊 Say it aloud before grading
      </p>

      {/* Self-grade buttons — binary correct/incorrect.
          stopPropagation on BOTH click and keydown prevents the parent
          flashcard div's onClick={onFlip} / onKeyDown (Enter/Space) from
          also firing — which would flip the just-advanced next card to
          its back face before the user sees the front. */}
      <div
        className="flex gap-3 justify-center"
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.stopPropagation();
          }
        }}
      >
        <button
          type="button"
          onClick={e => {
            e.stopPropagation();
            signals.updateWordSRS(card.id, false);
            signals.nextCard();
          }}
          className="flex-1 max-w-[180px] px-4 py-3 rounded-full text-sm font-semibold bg-incorrect-light text-incorrect hover:opacity-80 transition-all"
        >
          Didn't know
        </button>
        <button
          type="button"
          onClick={e => {
            e.stopPropagation();
            signals.updateWordSRS(card.id, true);
            signals.nextCard();
          }}
          className="flex-1 max-w-[180px] px-4 py-3 rounded-full text-sm font-semibold bg-correct-light text-correct hover:opacity-80 transition-all"
        >
          Got it
        </button>
      </div>
    </div>
  );
}
